"use client";

import type { HTMLAttributes, ReactNode, FC, Ref } from "react";

import type { IconProp } from "../../props";
import { Card, type CardProps } from "../Card/Card";
import { List } from "../List/List";
import { Icon } from "../Icon/Icon";
import { Text } from "../Text/Text";
import { Table } from "../Table/Table";
import { ListRow } from "../ListRow/ListRow";
import type { TableDataWithStyling, ValueType } from "../../types";
import { CardSubPanel } from "../CardSubPanel/CardSubPanel";
import { ValueParser } from "../ValueParser/ValueParser";
import { Flex } from "../Flex/Flex";

export type SummaryCardProps = HTMLAttributes<HTMLDivElement> &
  IconProp &
  Pick<CardProps, "border" | "columns" | "elevated"> & {
    title?: string;
    body?: ReactNode;
    value?: ValueType;
    variant?: "table" | "list";
    data: TableDataWithStyling;
    headerTrailing?: ReactNode;
    listVariant?: "default" | "compact";
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * SummaryCard component is a card component that displays a summary of data.
 */
export const SummaryCard: FC<SummaryCardProps> = ({
  className,
  icon,
  title,
  value,
  body,
  columns,
  border = "default",
  variant = "table",
  data,
  headerTrailing,
  listVariant = "default",
  children,
  ref,
  ...props
}) => (
  <Card
    ref={ref}
    className={className}
    border={border}
    columns={columns}
    {...props}
  >
    {(title || value || icon || headerTrailing) && (
      <CardSubPanel
        title={title}
        value={value}
        icon={icon}
        trailing={headerTrailing}
      />
    )}

    {body && (
      <Text variant="bodyS" color="currentColor50">
        {body}
      </Text>
    )}

    {variant === "table" ? (
      <Table data={data} />
    ) : (
      <List variant={listVariant}>
        {data?.rows?.[0]?.values?.map((row, i) => (
          <ListRow
            key={i}
            leading={
              columns === "two" &&
              (listVariant === "default" || listVariant === undefined) ? (
                <Icon icon={row.icon ?? "check"} />
              ) : row.icon ? (
                <Icon icon={row.icon} />
              ) : null
            }
            size={
              columns === "two" && listVariant === "default" ? "m" : "plain"
            }
            trailing={
              <Flex gap="xxs" align="center">
                <Text bold={listVariant !== "compact"}>
                  <ValueParser value={row.value} />
                </Text>
                {row.trailing}
              </Flex>
            }
            variant={listVariant === "compact" ? "compact" : "default"}
          >
            {data?.headings?.[i]?.name}
          </ListRow>
        ))}
      </List>
    )}
    {children}
  </Card>
);
